// WellnessBuddyLaunchModal.jsx
// Requires: react, prop-types, reactstrap@^8, classnames
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, Button, Row, Col, Badge } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import cx from 'classnames';
import heroImg from './wellness-buddy-pocket.jpg';
import { track } from 'lib/track';

/**
 * WellnessBuddyLaunchModal
 *
 * Props:
 * - isOpen: boolean — controls visibility
 * - onClose: fn — called when modal closes
 * - onStart: fn — called when user clicks the primary CTA
 * - imageSrc: string — header/hero image URL (prefer 2x size)
 * - title: string — main heading
 * - subtitle: string — supporting copy (centered)
 * - features: string[] — 3 short bullets rendered as badges/rows
 * - ctaText: string — primary button text
 * - secondaryText: string — optional secondary action label (link-style)
 * - onSecondary: fn — optional handler for secondary action
 * - persistKey: string — localStorage key to suppress re-show after accept/dismiss
 * - dense: boolean — compact layout variant (default: false = rich)
 * - brandColor: string — hex/rgb for CTA + accents (fallback: #2E7D32)
 * - testId: string — for testing selectors
 */
export default function WellnessBuddyLaunchModal({
  onClose,
  onStart,
  title = 'Meet Your New Wellness Buddy',
  subtitle = 'Your 24/7 resource for recipes, meal planning, live events, and questions about healthy, plant-powered living — all in one place.',
  features = [],
  ctaText = 'Start Chatting',
  secondaryText = 'Maybe later',
  onSecondary,
  persistKey = 'wb_launch_seen_v1',
  dense = false,
  brandColor = '#2E7D32',
  testId = 'wb-launch-modal'
}) {
  const [isOpen, setIsOpen] = useState(false);
  // Persist “seen” flag on close / start if persistKey is provided.
  const persistSeen = useMemo(
    () => () => {
      if (persistKey) localStorage.setItem(persistKey, '1');
    },
    [persistKey]
  );

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
    track('Modal Closed', { testId: testId });
  };

  const handleStart = () => {
    setIsOpen(false);
    onStart?.();
    track('Button Clicked', { title: ctaText, testId: `${testId}-cta` });
  };

  const handleSecondary = () => {
    onSecondary ? onSecondary() : onClose?.();
    setIsOpen(false);
    track('Button Clicked', { title: secondaryText, testId: `${testId}-cta` });
  };

  useEffect(() => {
    const seen = localStorage.getItem(persistKey) === '1';
    setIsOpen(!seen);
    persistSeen();
  }, [persistKey]);

  // Trap body scroll when open (reactstrap does, but this ensures parity in portals)
  useEffect(() => {
    if (!isOpen) return;
    track('Modal Opened', { testId: testId });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);
  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      centered
      fade
      contentClassName={cx('wb-modal', { 'wb-modal--dense': dense })}
      data-testid={testId}
      aria-labelledby="wb-modal-title"
      aria-describedby="wb-modal-subtitle"
    >
      <style>{`
        .wb-modal {
          border-radius: 16px;
          overflow: hidden;
          border: 0;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
        }
        .wb-hero {
          position: relative;
          margin-top: 2rem;
        }
        .wb-hero img {
          height: 33vh;
          display: block;
          margin:0 auto;
        }
        .wb-body {
          padding: 24px 24px 16px;
        }
        @media (min-width: 768px) {
          .wb-body { padding: 28px 32px 20px; }
        }
        .wb-title {
          font-weight: 700;
          line-height: 1.2;
          text-align: center;
          margin: 0 0 8px;
          font-size: clamp(22px, 3.3vw, 32px);
          letter-spacing: 0.2px;
          color: #1B4332;
        }
        .wb-subtitle {
          text-align: center;
          color: #2F3E46;
          margin: 0 auto 16px;
          max-width: 46ch;
          font-size: 16px;
        }
        .wb-badges {
          display: flex;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
          margin: 8px 0 0;
        }
        .wb-badge {
          background: rgba(46,125,50,0.08);
          color: #1B4332;
          border: 1px solid rgba(46,125,50,0.15);
          font-weight: 500;
          padding: 8px 12px;
          border-radius: 999px;
        }
        .wb-cta {
          text-align: center;
          margin-top: 20px;
        }
        
        .wb-footnote {
          margin-top: 8px;
          text-align: center;
          font-size: 12px;
          color: #5C6B66;
        }
        .wb-modal--dense .wb-badges { display: none; }
        .wb-modal--dense .wb-subtitle { margin-bottom: 6px; }
      `}</style>

      {/* Hero */}
      <div className="wb-hero">
        <img src={heroImg} alt="" />
      </div>

      {/* Body */}
      <ModalBody className="wb-body">
        <h2 id="wb-modal-title" className="wb-title">
          {title}
        </h2>

        <p id="wb-modal-subtitle" className="wb-subtitle">
          {subtitle}
        </p>

        {/* Feature badges (hidden in dense mode) */}
        {features?.length > 0 && (
          <div className="wb-badges" aria-label="Key capabilities">
            <ul>
              {features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA row */}
        <div className="wb-cta">
          <div>
            <Button
              color="primary"
              onClick={handleStart}
              data-testid={`${testId}-cta`}
              size="lg"
              style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
            >
              <FontAwesomeIcon icon={faSparkles} />
              &nbsp;{ctaText}
            </Button>
          </div>
          <div>
            <Button
              color="link"
              onClick={handleSecondary}
              data-testid={`${testId}-secondary`}
              style={{ margin: '0.5rem 0' }}
            >
              {secondaryText}
            </Button>
          </div>
        </div>

        {/* Optional “where to find it” hint */}
        {!dense && (
          <div className="wb-footnote">
            Tip: You can open Wellness Buddy anytime from the <FontAwesomeIcon icon={faSparkles} /> button in the
            lower-right.
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

WellnessBuddyLaunchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onStart: PropTypes.func,
  imageSrc: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
  ctaText: PropTypes.string,
  secondaryText: PropTypes.string,
  onSecondary: PropTypes.func,
  persistKey: PropTypes.string,
  dense: PropTypes.bool,
  brandColor: PropTypes.string,
  testId: PropTypes.string
};
